/**
 * NySak - Skjema for å opprette ny sak
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './NySak.css';

export default function NySak({ navigerTil }) {
  const [loading, setLoading] = useState(false);
  const [klienter, setKlienter] = useState([]);
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);

  const [formData, setFormData] = useState({
    klient_id: '',
    saksnummer: '',
    tittel: '',
    sakstype: 'avhendingsloven',
    status: 'aktiv',
    prioritet: 'normal',
    saksbeskrivelse: '',
    motpart_navn: '',
    motpart_org_nr: '',
    er_forsikringssak: false,
    forsikringsselskap: '',
    skadenummer: '',
    klient_har_rettshjelp: false,
    rettshjelp_selskap: '',
    motpart_er_forsikret: false,
    overtakelsesdato: '',
    mangel_oppdaget_dato: ''
  });

  useEffect(() => {
    lastKlienter();
    // Auto-generer saksnummer
    genererSaksnummer();
  }, []);

  const lastKlienter = async () => {
    try {
      const data = await crmApi.getKlienter();
      setKlienter(data.klienter || []);
    } catch (error) {
      console.error('Feil ved lasting av klienter:', error);
      alert('Kunne ikke laste klienter');
    }
  };

  const genererSaksnummer = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const time = String(now.getHours()).padStart(2, '0') + String(now.getMinutes()).padStart(2, '0');
    const saksnummer = `${year}-${month}${day}-${time}`;
    setFormData(prev => ({ ...prev, saksnummer }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.klient_id) {
      alert('Vennligst velg en klient');
      return;
    }

    if (!formData.saksnummer || !formData.tittel) {
      alert('Saksnummer og tittel er påkrevd');
      return;
    }

    setLoading(true);

    try {
      const sakData = {
        ...formData,
        er_forsikringssak: formData.er_forsikringssak ? 1 : 0,
        klient_har_rettshjelp: formData.klient_har_rettshjelp ? 1 : 0,
        motpart_er_forsikret: formData.motpart_er_forsikret ? 1 : 0
      };

      const result = await crmApi.createSak(sakData);
      alert('✅ Sak opprettet!');

      // Naviger til saken
      if (navigerTil) {
        navigerTil('sak-detaljer', result.id);
      } else {
        window.location.href = `/saker/${result.id}`;
      }
    } catch (error) {
      console.error('Feil ved opprettelse av sak:', error);
      alert('Kunne ikke opprette sak: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAvbryt = () => {
    if (navigerTil) {
      navigerTil('saker');
    } else {
      window.location.href = '/saker';
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="ny-sak">
      <div className="ny-sak-header">
        <h1>📝 Opprett ny sak</h1>
        <button className="btn-secondary" onClick={handleAvbryt}>
          ← Tilbake til saker
        </button>
      </div>

      <form onSubmit={handleSubmit} className="ny-sak-form">
        {/* Grunnleggende informasjon */}
        <section className="form-section">
          <h2>Grunnleggende informasjon</h2>

          <div className="form-group required">
            <label>Klient *</label>
            <select
              value={formData.klient_id}
              onChange={(e) => handleInputChange('klient_id', e.target.value)}
              required
            >
              <option value="">-- Velg klient --</option>
              {klienter.map(k => (
                <option key={k.id} value={k.id}>
                  {k.navn} {k.org_nr ? `(${k.org_nr})` : ''}
                </option>
              ))}
            </select>
            {klienter.length === 0 && (
              <p className="form-hint">
                ⚠️ Du har ingen klienter. Opprett en klient først.
              </p>
            )}
          </div>

          <div className="form-row">
            <div className="form-group required">
              <label>Saksnummer *</label>
              <input
                type="text"
                value={formData.saksnummer}
                onChange={(e) => handleInputChange('saksnummer', e.target.value)}
                required
                placeholder="2026-0401-1234"
              />
              <button
                type="button"
                className="btn-link"
                onClick={genererSaksnummer}
              >
                🔄 Generer nytt
              </button>
            </div>

            <div className="form-group required">
              <label>Sakstype *</label>
              <select
                value={formData.sakstype}
                onChange={(e) => handleInputChange('sakstype', e.target.value)}
                required
              >
                <option value="avhendingsloven">Avhendingsloven</option>
                <option value="bustadoppføringslova">Bustadoppføringslova</option>
                <option value="entreprise">Entreprise</option>
                <option value="eiendomsmegling">Eiendomsmegling</option>
                <option value="naboforhold">Naboforhold</option>
                <option value="annet">Annet</option>
              </select>
            </div>
          </div>

          <div className="form-group required">
            <label>Sakstittel *</label>
            <input
              type="text"
              value={formData.tittel}
              onChange={(e) => handleInputChange('tittel', e.target.value)}
              required
              placeholder="F.eks. Kjøpersak - Fuktskader i bod"
            />
          </div>

          <div className="form-group">
            <label>Saksbeskrivelse</label>
            <textarea
              value={formData.saksbeskrivelse}
              onChange={(e) => handleInputChange('saksbeskrivelse', e.target.value)}
              rows="4"
              placeholder="Kort beskrivelse av saken..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                <option value="aktiv">Aktiv</option>
                <option value="venter">Venter på svar</option>
                <option value="arkivert">Arkivert</option>
                <option value="avsluttet">Avsluttet</option>
              </select>
            </div>

            <div className="form-group">
              <label>Prioritet</label>
              <select
                value={formData.prioritet}
                onChange={(e) => handleInputChange('prioritet', e.target.value)}
              >
                <option value="lav">Lav</option>
                <option value="normal">Normal</option>
                <option value="høy">Høy</option>
                <option value="kritisk">Kritisk</option>
              </select>
            </div>
          </div>
        </section>

        {/* Motpart */}
        <section className="form-section">
          <h2>Motpart</h2>

          <div className="form-row">
            <div className="form-group">
              <label>Motpartens navn</label>
              <input
                type="text"
                value={formData.motpart_navn}
                onChange={(e) => handleInputChange('motpart_navn', e.target.value)}
                placeholder="Selger, entreprenør, etc."
              />
            </div>

            <div className="form-group">
              <label>Org.nr / Fødselsnummer</label>
              <input
                type="text"
                value={formData.motpart_org_nr}
                onChange={(e) => handleInputChange('motpart_org_nr', e.target.value)}
                placeholder="123456789"
              />
            </div>
          </div>

          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.motpart_er_forsikret}
                onChange={(e) => handleInputChange('motpart_er_forsikret', e.target.checked)}
              />
              <span>Motpart er forsikret</span>
            </label>
          </div>
        </section>

        {/* Datoer */}
        <section className="form-section">
          <h2>Viktige datoer</h2>

          <div className="form-row">
            <div className="form-group">
              <label>Overtakelsesdato</label>
              <input
                type="date"
                value={formData.overtakelsesdato}
                onChange={(e) => handleInputChange('overtakelsesdato', e.target.value)}
              />
              <p className="form-hint">For avhendingsloven/bustadoppføringslova</p>
            </div>

            <div className="form-group">
              <label>Mangel oppdaget dato</label>
              <input
                type="date"
                value={formData.mangel_oppdaget_dato}
                onChange={(e) => handleInputChange('mangel_oppdaget_dato', e.target.value)}
              />
              <p className="form-hint">Når ble mangelen først oppdaget?</p>
            </div>
          </div>
        </section>

        {/* Forsikring */}
        <section className="form-section">
          <h2>Forsikring</h2>

          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.er_forsikringssak}
                onChange={(e) => handleInputChange('er_forsikringssak', e.target.checked)}
              />
              <span>Dette er en forsikringssak</span>
            </label>
          </div>

          {formData.er_forsikringssak && (
            <div className="form-row">
              <div className="form-group">
                <label>Forsikringsselskap</label>
                <input
                  type="text"
                  value={formData.forsikringsselskap}
                  onChange={(e) => handleInputChange('forsikringsselskap', e.target.value)}
                  placeholder="F.eks. If, Gjensidige"
                />
              </div>

              <div className="form-group">
                <label>Skadenummer</label>
                <input
                  type="text"
                  value={formData.skadenummer}
                  onChange={(e) => handleInputChange('skadenummer', e.target.value)}
                  placeholder="Referansenummer fra forsikringsselskap"
                />
              </div>
            </div>
          )}
        </section>

        {/* Rettshjelp */}
        <section className="form-section">
          <h2>Rettshjelp</h2>

          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.klient_har_rettshjelp}
                onChange={(e) => handleInputChange('klient_har_rettshjelp', e.target.checked)}
              />
              <span>Klient har rettshjelp</span>
            </label>
          </div>

          {formData.klient_har_rettshjelp && (
            <div className="form-group">
              <label>Rettshjelpselskap</label>
              <input
                type="text"
                value={formData.rettshjelp_selskap}
                onChange={(e) => handleInputChange('rettshjelp_selskap', e.target.value)}
                placeholder="F.eks. If, Protector"
              />
              <p className="form-hint">
                ℹ️ Du kan legge til detaljert rettshjelp-informasjon senere i saksdetaljene
              </p>
            </div>
          )}
        </section>

        {/* Knapper */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleAvbryt}
            disabled={loading}
          >
            Avbryt
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading || !formData.klient_id}
          >
            {loading ? 'Oppretter...' : '✓ Opprett sak'}
          </button>
        </div>
      </form>
    </div>
  );
}
